import constate from 'constate';
import { merge } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { TimeSeriesChartVisualOptions } from '@/core';
import { useDataQueryResult } from '@/plugin-system';
import { DEFAULT_VISUAL } from '../time-series-chart-model';

type VisualSettingsProviderProps = {
  visual?: TimeSeriesChartVisualOptions;
};

/**
 * Processes visual spec acccounting for default values.
 * Stabilizes the visual prop to prevent flicker when visual settings change before data has finished loading.
 */
function useVisualSettingsInternal({ visual: visualProp }: VisualSettingsProviderProps): TimeSeriesChartVisualOptions {
  const { queryResults, isFetching } = useDataQueryResult({ dataTypeFilter: 'TimeSeries' });
  const hasData = queryResults.some((result) => result.data && result.data.series.length > 0);

  // This prevents the brief moment when the chart shows the new visual settings before the data updates.
  const visualRef = useRef(visualProp);
  useEffect(() => {
    if (!isFetching && hasData) {
      visualRef.current = visualProp;
    }
  }, [isFetching, hasData, visualProp]);

  const visual = isFetching ? visualRef.current : visualProp;

  const mergedVisual = useMemo(() => {
    return merge({}, DEFAULT_VISUAL, visual);
  }, [visual]);
  return mergedVisual;
}

export const [VisualSettingsProvider, useVisualSettings] = constate(useVisualSettingsInternal);
